import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import SvgIcon from '@mui/material/SvgIcon';
import Popover from '@mui/material/Popover';
import InputBase from '@mui/material/InputBase';
import IconButton from '@mui/material/IconButton';
import DateRange from '@mui/icons-material/DateRange';
import ArrowDropUp from '@mui/icons-material/ArrowDropUp';
import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import moment, { MomentInput } from 'moment';
import { DATA_PICKER_CLASSNAME } from '@/constants/component-clssname';
import { getYear, getMonth, getDay } from '@/utils/datetime';

export type CalendarPickerView = 'year' | 'day' | 'month';
export const DATE_IS_CURRENT = 'is_current';
export const DATE_IS_CURRENT_CHINESE = '至今';

interface DatePickerProps<TInputDate, TDate extends MomentInput> {
  label?: string;
  views?: readonly CalendarPickerView[];
  format?: string;
  value: TInputDate;
  onChange: (value: string) => void;
  maxDate?: TDate;
  minDate?: TDate;
  className?: string;
  placeholder?: string;
  sx?: React.CSSProperties;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  disableFuture?: boolean;
  labelColor?: string;
  showCurrent?: boolean;
  onOpen?: () => void;
  dateClassName?: string;
}

const DatePicker = <
  TInputDate extends MomentInput,
  TDate extends MomentInput = TInputDate,
>(
  props: DatePickerProps<TInputDate, TDate>,
) => {
  const {
    value,
    views = [],
    format = '',
    className = '',
    minDate = moment('1900-01-01'),
    maxDate = moment('2099-12-31'),
    onChange,
    placeholder = '选择时间',
    label = '',
    labelColor = 'text-black',
    sx = {},
    error = false,
    helperText = '',
    disabled = false,
    disableFuture = false,
    showCurrent = false,
    onOpen,
    dateClassName = '',
  } = props;

  const _value =
    (value === DATE_IS_CURRENT ? moment() : value) ||
    (moment() > moment(maxDate) ? moment(maxDate) : moment());
  const [open, setOpen] = React.useState(false);
  const [panel, setPanel] = React.useState('year');

  const formatList = [];
  if (views.includes('year')) {
    formatList.push('YYYY');
  }
  if (views.includes('month')) {
    formatList.push('MM');
  }
  if (views.includes('day')) {
    formatList.push('DD');
  }
  const _format = format
    ? format
    : formatList.length > 0
      ? formatList.join('-')
      : 'YYYY-MM-DD';

  const yearOfValue = getYear(_value, _format);
  const monthOfValue = getMonth(_value, _format);
  const dayOfValue = getDay(_value, _format);
  const currentYear = getYear();
  const currentMonth = getMonth();
  // const currentDay = getDay();

  const inputValue = value
    ? value === DATE_IS_CURRENT
      ? DATE_IS_CURRENT_CHINESE
      : moment(value, _format).format(_format)
    : '';
  const anchorRef = React.useRef<null | HTMLDivElement>(null);
  const yearElRef = React.useRef<null | HTMLButtonElement>(null);
  const popoverRef = React.useRef(null);

  React.useEffect(() => {
    if (open && panel === 'year') {
      setTimeout(
        () =>
          yearElRef.current?.scrollIntoView({
            block: 'center',
          }),
        10,
      );
    }
  }, [open, yearOfValue, panel]);

  const isYearOnlyView = views.length === 1 && views.includes('year');
  const isYearAndMonthViews =
    views.length === 2 && views.includes('year') && views.includes('month');

  const handleClickArrowIcon = () => {
    if (panel === 'year') {
      setPanel('month');
    } else {
      setPanel('year');
    }
  };

  const handleClose = () => {
    setOpen(false);
    setTimeout(() => {
      setPanel('year');
    }, 300);
  };

  const handleNext = () => {
    if (
      (isYearOnlyView && panel === 'year') ||
      (isYearAndMonthViews && panel === 'month') ||
      (!isYearOnlyView && !isYearAndMonthViews && panel === 'day')
    ) {
      handleClose();
    }
    if (!isYearOnlyView && panel === 'year') setPanel('month');
    if (!isYearOnlyView && !isYearAndMonthViews && panel === 'month') {
      setPanel('day');
    }
  };

  const handleChange = (value: Date) => {
    onChange(moment(value).format(_format));
  };

  const renderYearPanel = () => {
    const minYear = getYear(minDate);
    const maxYear = getYear(maxDate);
    const minMonth = getMonth(minDate);
    const maxMonth = getMonth(maxDate);
    const yearList = [];
    for (let y = minYear; y <= maxYear; y++) {
      yearList.push(y);
    }
    return (
      <div className="grid grid-cols-3 gap-x-12 gap-y-4 tablet:gap-x-14">
        {yearList.map((yItem) => {
          const disabled = disableFuture ? yItem > currentYear : false;
          return (
            <Button
              key={yItem}
              onClick={() => {
                handleChange(
                  new Date(
                    yItem,
                    yItem === minYear || yItem < maxYear
                      ? monthOfValue - 1
                      : monthOfValue < minMonth || monthOfValue > maxMonth
                        ? maxMonth - 1
                        : monthOfValue - 1,
                    dayOfValue,
                  ),
                );
                handleNext();
              }}
              sx={{
                fontSize: 14,
                lineHeight: 1.5,
                padding: '8px 16px',
                borderRadius: '20px',
                width: 'fit-content',
                color:
                  yItem === yearOfValue && value !== DATE_IS_CURRENT
                    ? 'white'
                    : '#000',
              }}
              variant={
                yItem === yearOfValue && value !== DATE_IS_CURRENT
                  ? 'contained'
                  : 'text'
              }
              ref={yItem === yearOfValue ? yearElRef : null}
              disabled={disabled}
            >
              {yItem}
            </Button>
          );
        })}
      </div>
    );
  };

  const renderMonthPanel = () => {
    const minYear = getYear(minDate);
    const minMonth = getMonth(minDate);
    const maxYear = getYear(maxDate);
    const maxMonth = getMonth(maxDate);
    return (
      <div className="grid grid-cols-3 gap-x-12 gap-y-4 tablet:gap-x-14">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((mItem) => {
          let disabled = false;
          if (
            yearOfValue === minYear &&
            yearOfValue === maxYear &&
            minMonth <= maxMonth
          ) {
            minMonth === maxMonth
              ? (disabled = mItem != maxMonth)
              : (disabled = mItem > maxMonth);
          } else if (yearOfValue === minYear) {
            disabled = mItem < minMonth;
          } else if (yearOfValue === maxYear) {
            disabled = mItem > maxMonth;
          } else if (disableFuture && yearOfValue === currentYear) {
            disabled = mItem > currentMonth;
          }
          return (
            <Button
              key={mItem}
              sx={{
                fontSize: 14,
                lineHeight: 1.5,
                padding: '8px 16px',
                borderRadius: '20px',
                width: 'fit-content',
                color: mItem === monthOfValue ? 'white' : '#000',
              }}
              onClick={() => {
                handleChange(new Date(yearOfValue, mItem - 1, dayOfValue));
                handleNext();
              }}
              variant={mItem === monthOfValue ? 'contained' : 'text'}
              disabled={disabled}
            >
              {mItem}月
            </Button>
          );
        })}
      </div>
    );
  };

  const renderDayPanel = () => {
    // TODO: work for day panel in future
    return null;
  };

  React.useEffect(() => {
    if (open) {
      onOpen?.();
    }
  }, [open]);

  return (
    <Box className={`flex flex-col ${className}`} sx={sx}>
      {label && (
        <label
          className={`text-sm leading-5.5 font-semibold ${labelColor} mb-2 tablet:mb-3`}
        >
          {label}
        </label>
      )}
      <div
        ref={anchorRef}
        className={`${DATA_PICKER_CLASSNAME}${dateClassName ? ` ${dateClassName}` : ''} rounded-lg inline-flex items-center rounded relative bg-slate-100 text-sm w-auto py-3.5 px-4 justify-between max-h-[56px] border border-transparent ${disabled ? 'cursor-not-allowed tablet:hover:bg-slate-50' : 'tablet:hover:bg-slate-100'} tablet:bg-slate-50`}
        onClick={() => !disabled && setOpen((prev) => !prev)}
      >
        <div className="inline-flex grow items-center">
          <DateRange
            className={disabled ? '' : 'cursor-pointer'}
            sx={{ color: '#000', opacity: disabled ? 0.42 : 1 }}
          />
          <InputBase
            sx={{
              color: error ? '#b91c1c' : '#000',
              fontSize: 14,
              lineHeight: 1.5,
            }}
            className="ml-3"
            value={inputValue}
            placeholder={placeholder}
            disabled={disabled}
            inputProps={{
              className:
                disabled && error
                  ? 'disabled:text-red-700 disabled:[-webkit-text-fill-color:#b91c1c]'
                  : undefined,
            }}
            readOnly // if allow user to input date, remove this attr
            fullWidth
          />
        </div>
        <SvgIcon sx={{ color: '#79747e' }}>
          {open ? <ArrowDropUp /> : <ArrowDropDown />}
        </SvgIcon>
      </div>
      {error && helperText && (
        <span className="text-red-700 text-xs mt-2">{helperText}</span>
      )}
      <Popover
        anchorEl={anchorRef.current}
        open={open}
        ref={popoverRef}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        onClose={handleClose}
        sx={{
          '.MuiPaper-root': {
            borderRadius: '20px',
          },
        }}
      >
        <div className="overflow-hidden bg-slate-50 shadow-[0_10px_20px_rgb(0,0,0,0.05)] max-h-[470px]">
          {!isYearOnlyView && (
            <div className="flex p-10 justify-center items-center bg-blue-400 z-10 h-[112px]">
              <span className="text-white text-2xl font-semibold">
                {yearOfValue}年{monthOfValue}月
              </span>
              <IconButton onClick={handleClickArrowIcon}>
                <SvgIcon htmlColor="white">
                  {panel === 'year' ? <ArrowDropDown /> : <ArrowDropUp />}
                </SvgIcon>
              </IconButton>
            </div>
          )}
          <div className="p-6 overflow-y-auto max-h-[358px] tablet:px-10">
            {panel === 'year' && renderYearPanel()}
            {panel === 'month' && renderMonthPanel()}
            {panel === 'day' && renderDayPanel()}
            {showCurrent && (
              <div
                data-selected={value === DATE_IS_CURRENT}
                className="text-center text-sm py-2 mt-4 cursor-pointer rounded-lg hover:bg-web-blue-500/[0.04]"
                onClick={() => {
                  onChange(DATE_IS_CURRENT);
                  handleClose();
                }}
              >
                {DATE_IS_CURRENT_CHINESE}
              </div>
            )}
          </div>
        </div>
      </Popover>
    </Box>
  );
};

export default DatePicker;
